"use client";

import React, { useCallback, useState } from "react";
import { toast } from "sonner";

//

export interface UseHandlerOptions<T> {
  disableSuccessMessage?: boolean;
  successMessage?: React.ReactNode | ((data: T) => React.ReactNode);
  successDescription?: React.ReactNode | ((data: T) => React.ReactNode);
  errorMessage?: React.ReactNode | ((error: unknown) => React.ReactNode);
  errorDescription?: React.ReactNode | ((error: unknown) => React.ReactNode);
  finally?: () => void;
}

//

/**
 *
 * @deprecated Use useHandler instead with new return
 */

export const useHandlerDepracated = <Params extends unknown[], T>(
  callback: (...p: Params) => Promise<T> | T,
  options: UseHandlerOptions<T> = {},
) => {
  const {
    successMessage = "Сохранено",
    successDescription,
    errorMessage = "Ошибка",
    errorDescription,
    finally: finallyCallback,
    disableSuccessMessage,
  } = options;

  const _callback = useCallback(
    async (...p: Params) => {
      try {
        const result = await callback(...p);
        if (!disableSuccessMessage) {
          const message =
            typeof successMessage === "function" ? successMessage(result) : successMessage;
          const description =
            typeof successDescription === "function"
              ? successDescription(result)
              : successDescription;
          if (message || description) toast.success(message, { description: description });
        }
      } catch (error) {
        const message = typeof errorMessage === "function" ? errorMessage(error) : errorMessage;
        const description =
          errorDescription === undefined
            ? error instanceof Error
              ? error.message
              : JSON.stringify(error, null, 2)
            : typeof errorDescription === "function"
              ? errorDescription(error)
              : errorDescription;
        toast.error(message, { description });
        console.error(error);
      } finally {
        if (finallyCallback) finallyCallback();
      }
    },
    [
      callback,
      disableSuccessMessage,
      errorDescription,
      errorMessage,
      finallyCallback,
      successDescription,
      successMessage,
    ],
  );

  return _callback;
};

//

export const useHandler = <Params extends unknown[], T>(
  callback: (...p: Params) => Promise<T> | T,
  options: UseHandlerOptions<T> = {},
) => {
  const [isLoading, setLoading] = useState(false);

  const handler = useHandlerDepracated<Params, T>(
    (...p) => {
      setLoading(true);
      return callback(...p);
    },
    {
      ...options,
      finally: () => {
        if (options.finally) options.finally();
        setLoading(false);
      },
    },
  );

  return { handler, isLoading };
};
